import { Fuel, PendingTopup, TopupHistoryItem } from '@/src/api/payg/types';

export type TopupCopyAction = 'webchat' | 'call' | 'manualTopupHelp';

export const TOPUP_COPY_ACTIONS: Record<
  TopupCopyAction,
  { label: string; href: string }
> = {
  webchat: {
    label: 'Start webchat',
    href: 'https://www.ovoenergy.com/help',
  },
  call: {
    label: 'Call support',
    href: 'tel:03303035063',
  },
  manualTopupHelp: {
    label: 'Read how to top up manually',
    href: 'https://www.ovoenergy.com/help/article/topping-up-my-meter',
  },
};

/**
 * Shown alongside the amount on the history row and the detail header. The
 * non-breaking space keeps "Support Credit" together when the label wraps.
 */
export const ASC_LABEL = 'Additional Support\u00A0Credit';

export type TopupCopy = {
  title?: string;
  message?: string;
  nextStep?: string;
  buttons?: TopupCopyAction[];
};

export type TopupCopyVariants = {
  default: TopupCopy;
  asc?: TopupCopy;
};

/**
 * Copy that needs a value from the top-up is written as a function of that
 * value, so the variable is typed where the copy is written. Copy that needs
 * nothing stays a plain string.
 */
export type TopupCopyText<T> = string | ((value: T) => string);

/** As `TopupCopy`, but the title may be named after the top-up's fuel. */
export type TopupNotificationCopy = Omit<TopupCopy, 'title'> & {
  title?: TopupCopyText<Fuel>;
};

export type TopupNotificationCopyVariants = {
  default: TopupNotificationCopy;
  asc?: TopupNotificationCopy;
};

export type TopupDetailState = Exclude<
  TopupHistoryItem['detailedState'],
  'PROCESSING'
>;

export const TOPUP_DETAIL_COPY: Record<TopupDetailState, TopupCopyVariants> = {
  SUCCESS: {
    default: {},
  },
  VEND_FAILED_NO_ACTION: {
    default: {
      title: 'There’s a technical problem',
      message:
        'The payment is being held by your bank and will be released within 5 working days.',
    },
    asc: {
      title: 'There’s a technical problem',
      message: 'Your support credit has not been added to your meter.',
      nextStep: 'Contact us to get your support credit added.',
      buttons: ['webchat', 'call'],
    },
  },
  VEND_FAILED_CONTACT_US: {
    default: {
      title: 'There’s a crediting issue',
      message:
        'Your credit has not been added to your meter. The payment is being held by your bank and will be released within 5 working days.',
      nextStep:
        'Contact us and quote this error code to help us fix this issue.',
      buttons: ['webchat', 'call'],
    },
    asc: {
      title: 'There’s a crediting issue',
      message: 'Your support credit has not been added to your meter.',
      nextStep:
        'Contact us and quote this error code to help us fix this issue.',
      buttons: ['webchat', 'call'],
    },
  },
  APPLY_FAILED_CONTACT_US: {
    default: {
      title: 'There’s a crediting issue',
      message:
        'Your payment has been taken but your credit has not been added to your meter.',
      nextStep:
        'Contact us and quote this error code to help us fix this issue.',
      buttons: ['webchat', 'call'],
    },
    asc: {
      title: 'There’s a crediting issue',
      message:
        'Your support credit has been approved, but we couldn’t add it to your meter.',
      nextStep:
        'Contact us and quote this error code to help us fix this issue.',
      buttons: ['webchat', 'call'],
    },
  },
  APPLY_FAILED_NO_ACTION: {
    default: {
      title: 'You need to manually enter your code',
      message:
        'Your payment has been taken, but there’s a problem adding the credit to your meter.',
      nextStep:
        'Enter your code manually on your meter or your In-Home Display (IHD)',
      buttons: ['manualTopupHelp'],
    },
    asc: {
      title: 'You need to manually enter your code',
      message:
        'Your support credit has been approved, but we couldn’t add it to your meter.',
      nextStep:
        'Enter your code manually on your meter or your In-Home Display (IHD)',
      buttons: ['manualTopupHelp'],
    },
  },
};

export type TopupNotificationState = Extract<
  PendingTopup['state'],
  | 'TRY_AGAIN_LATER'
  | 'CONTACT_US'
  | 'CONTACT_US_APPLY_FAILED'
  | 'MANUALLY_ENTER_UTRN'
  | 'SUCCESSFULLY_APPLIED'
>;

/**
 * Most notifications say the same thing as a detail state, so they share the
 * copy and cannot drift apart.
 */
export const TOPUP_NOTIFICATION_COPY: Record<
  TopupNotificationState,
  TopupNotificationCopyVariants
> = {
  TRY_AGAIN_LATER: TOPUP_DETAIL_COPY.VEND_FAILED_NO_ACTION,
  CONTACT_US: TOPUP_DETAIL_COPY.VEND_FAILED_CONTACT_US,
  CONTACT_US_APPLY_FAILED: TOPUP_DETAIL_COPY.APPLY_FAILED_CONTACT_US,
  MANUALLY_ENTER_UTRN: TOPUP_DETAIL_COPY.APPLY_FAILED_NO_ACTION,
  SUCCESSFULLY_APPLIED: {
    default: {
      title: fuel => `${fuel} meter topped up`,
      message: 'Your credit’s been added to your balance.',
    },
    asc: {
      title: fuel => `${fuel} meter topped up`,
      message: 'Your support credit’s been added to your balance.',
    },
  },
};

export type TopupProcessingState = Extract<
  PendingTopup['state'],
  'PENDING_PAYMENT' | 'PROCESSING' | 'AUTO_APPLY_DELAYED'
>;

export const TOPUP_PROCESSING_COPY = {
  title: (fuel: Fuel) => `Topping up your ${fuel.toLowerCase()} meter`,
  messages: {
    PENDING_PAYMENT: 'Your balance will be updated shortly',
    PROCESSING: (minutes: number) =>
      `Please bear with us – adding credit can take up to ${minutes} minutes.`,
    AUTO_APPLY_DELAYED:
      'Please bear with us – adding credit is taking longer than expected.',
  },
};
